<template>
  <div
    class="group-param-page fullscreen-fixed flex-center"
    :style="{'background-color': $store.registratorBackgroundColor}"
  >
    <div class="group-param-page__container">
      <div
        v-if="$store.groupElements"
        class="group-param-page__caption"
        :style="{color: $store.registratorCaptionColor}"
      >
        {{ 'registrator_fill_form' | translate }}
      </div>
      <div v-if="$store.groupElements">
        <GroupParamElement
          v-for="(el, index) in $store.groupElements"
          :key="index"
          :is-active="index === currentParamIndex"
          :element="el"
          @focus="changeSelectedParam(index)"
        />
      </div>
    </div>
    <Keyboard
      v-model="updatedValue"
      :layout="keyboardLayout"
      :default-language-code="lang"
      :caps-on-init="caps"
      :shift-on-init="!isNumberLayout"
      :row-widths="getRowWidths"
      font-size="30px"
      width="100%"
      @cancel="onClickCancel"
      @next="onClickNext"
      @finish="onClickFinish"
      @languageChanged="lang = $event"
      @switchNumberKeyboard="toggleNumberLayout"
    />
  </div>
</template>

<script>
import GroupParamElement from './GroupParamElement';
import Keyboard from './keyboard/Keyboard';

import { GROUP_PARAMS_ERRORS } from "@/components/devices/Registrator/config";

const cancelButtonVariants = {
  ru: '{cancel | Отмена}',
  en: '{cancel | Cancel}',
  kk: '{cancel | Бас тарту}'
};
const spaceButtonVariants = {
  ru: '{space | Пробел}',
  en: '{space | Space}',
  kk: '{space | Бос орын}'
}
const finishButtonVariants = {
  ru: '{finish | Получить}',
  en: '{finish | Get Ticket}',
  kk: '{finish | Алу}'
};
const nextButtonVariants = {
  ru: '{next | Далее}',
  en: '{next | Next}',
  kk: '{next | Келесі}'
};

const numberLikeTypes = ['number', 'phone'];

export default {
  name: 'GroupParamsPage',
  components: {GroupParamElement, Keyboard},
  computed: {
    updatedValue: {
      get() {
        return this.$store.groupElements && this.$store.groupElements[this.currentParamIndex].data;
      },
      set(val) {
        if (!this.$store.groupElements || !this.$store.groupElements[this.currentParamIndex])
          return;
        
        const currentElement = this.$store.groupElements[this.currentParamIndex];
        

        if (currentElement.type === 'phone') {
          if (val === '' || !val.startsWith('+')) {
            currentElement.data = '+';
            return;
          }

          if (val.length < currentElement.data.length && !val.startsWith('+')) {
            currentElement.data = '+';
            return;
          }
        }
        
        this.$store.groupElements[this.currentParamIndex].data = val;
      }
    },
    lastKeyboardRow() {
      const isLastElement =
        this.$store.groupElements &&
        this.currentParamIndex === this.$store.groupElements.length - 1;
      return `${cancelButtonVariants[this.lang]} ${this.isNumberLayout ? '' : '{lang}'} ${spaceButtonVariants[this.lang]} ${this.isNumberLayout ? '' : '{bksp}'} ${
        isLastElement ? finishButtonVariants[this.lang] : nextButtonVariants[this.lang]
      }`;
    },
    useNumberKeyboardLayout() {
      if (!this.$store.groupElements || !this.$store.groupElements[this.currentParamIndex]) return false;
      return numberLikeTypes.includes(this.$store.groupElements[this.currentParamIndex].type);
    },
    isNumberLayout() {
      if (this.manualLayoutOverride !== null) return this.manualLayoutOverride;
      return this.useNumberKeyboardLayout;
    },
    keyboardLayout() {
      if (this.isNumberLayout) {
        if(this.lang === 'kk' && !this.useNumberKeyboardLayout) {
          return { [this.lang]: {
            default: [
              '1 2 3',
              '4 5 6',
              '7 8 9',
              '{switchNumberKeyboard | ABC} + 0 {bksp}',
              this.lastKeyboardRow,
            ],
          } }
        }
        return { [this.lang]: {
          default: [
            '1 2 3',
            '4 5 6',
            '7 8 9',
            '+ 0 {bksp}',
            this.lastKeyboardRow,
          ],
        } }
      }
      return {
        ru: {
          default: [
            'ё 1 2 3 4 5 6 7 8 9 0 , .',
            'й ц у к е н г ш щ з х ъ',
            'ф ы в а п р о л д ж э',
            '{shift} я ч с м и т ь б ю {shift}',
            this.lastKeyboardRow
          ],
          shift: [
            'Ё 1 2 3 4 5 6 7 8 9 0 , .',
            'Й Ц У К Е Н Г Ш Щ З Х Ъ',
            'Ф Ы В А П Р О Л Д Ж Э',
            '{shift} Я Ч С М И Т Ь Б Ю {shift}',
            this.lastKeyboardRow
          ],
          langName: 'Русский'
        },
        en: {
          default: [
            '1 2 3 4 5 6 7 8 9 0 , .',
            'q w e r t y u i o p',
            'a s d f g h j k l',
            '{shift} z x c v b n m {shift}',
            this.lastKeyboardRow
          ],
          shift: [
            '1 2 3 4 5 6 7 8 9 0 , .',
            'Q W E R T Y U I O P',
            'A S D F G H J K L',
            '{shift} Z X C V B N M {shift}',
            this.lastKeyboardRow
          ],
          langName: 'English'
        },
        kk: {
          default: [
            ') ә і ң ғ , . ү ұ қ ө һ',
            'й ц у к е н г ш щ з х ъ',
            'ф ы в а п р о л д ж э',
            '{switchNumberKeyboard | 123} я ч с м и т ь б ю {shift}',
            this.lastKeyboardRow
          ],
          shift: [
            '( Ә І Ң Ғ , . Ү Ұ Қ Ө Һ',
            'Й Ц У К Е Н Г Ш Щ З Х Ъ',
            'Ф Ы В А П Р О Л Д Ж Э',
            '{switchNumberKeyboard | 123} Я Ч С М И Т Ь Б Ю {shift}',
            this.lastKeyboardRow
          ],
          langName: 'Қазақ'
        }
      };
    },
    getRowWidths() {
      return this.isNumberLayout ? ['800px', '800px', '800px', '800px'] : undefined;
    },
    title() {
      return (
        (this.$store.groupParamTitles &&
          this.$store.groupParamTitles[this.$store.currentLanguage.tag]) ||
        this.$languageService.translateFilter('no_user')
      );
    }
  },
  data() {
    return {
      lang: this.$store.activeLanguage,
      caps: false,
      shift: true,
      currentParamIndex: 0,
      manualLayoutOverride: null
    };
  },
  beforeDestroy() {
    this.$groupParamsService.reset();
  },
  methods: {
    changeSelectedParam(index) {
      if (index > this.$store.groupElements.length - 1) {
        throw new Error("Can't select next param");
      }

      this.currentParamIndex = index;
      this.manualLayoutOverride = null;
    },
    onClickCancel() {
      this.$emit('cancel');
    },
    onClickNext() {
      this.changeSelectedParam(this.currentParamIndex + 1);
    },
    onClickFinish() {
      const validation = this.$groupParamsService.validateGroupParams();
      console.log('validation', validation);
      if (validation.error) {
        this.showError(validation);
      } else {
        this.$emit('complete');
      }
    },
    formatMessage(template, values = {}) {
      return template.replace(/{(\w+)}/g, (_, key) => {
        return key in values ? values[key] : `{${key}}`;
      });
    },
    showError(validation) {
      const paramName = this.getParamTranslation(validation.invalidElement);
      const translation = this.$languageService.translateFilter(`registrator_group_param_${validation.error}`);
      const length = validation.error === GROUP_PARAMS_ERRORS.MAX_LENGTH_ERROR
        || validation.error === GROUP_PARAMS_ERRORS.MIN_LENGTH_ERROR
        ? validation.invalidElement : ''
      const formatedErrorMessage = this.formatMessage(translation, { min: length.min_length - 1, max: length.length })
      const errorMessage = `${paramName}: ${formatedErrorMessage}`
      this.$root.$emit('showModal', { message: errorMessage });
    },
    getParamTranslation(element) {
      return element.kiosk_title[this.$store.activeLanguage] || Object.values(element.kiosk_title).find(val => val);
    },
    toggleNumberLayout() {
      this.manualLayoutOverride = !this.isNumberLayout;
    }
  }
};
</script>

<style lang="scss" scoped>
.group-param-page {
  flex-direction: column;
  height: calc(100vh - 14.453125vh);
  margin-top: 14.453125vh;
  //margin-top: min(9.765625vh, 100px);

  &__container {
    display: flex;
    flex-direction: column;
    width: 50%;
    min-width: 650px;
    flex-grow: 1;
  }

  &__caption {
    text-align: center;
    font-size: 30px;
    font-weight: 600;
    margin:2.222vh;
    padding: 0 32px;
  }
}

@media screen and (min-height: 1080px) {
  .group-param-page{
    &__caption{
      margin: 24px;
    }
  }
}
</style>
